<template>
  <div
    v-if="displayedTaskIds"
    :id="dropZoneId"
    v-hotkey="shortcuts"
    class="tasklist-container"
  >
    <DraggableContainer
      ref="taskListEl"
      v-click-outside="unselectTask"
      tag="div"
      class="itemslist taskslist"
      :class="{ isDraggingTask, isOutsideDropZone }"
    >
      <div
        v-if="title"
        class="title-container"
        :class="{ 'non-collapsable': !isCollapsable }"
      >
        <BaseButton
          v-if="isCollapsable"
          kind="auxiliary"
          type="secondary"
          class="collapse-button"
          @click="$emit('update:isCollapsed', !isCollapsed)"
        >
          <template #title>
            {{ title }}
            <span
              v-if="displayedTaskIds && displayedTaskIds.length > 0"
              class="task-count"
              :class="{ 'task-count--overdue': isOverdue }"
              >{{ displayedTaskIds.length }}</span
            >
          </template>
          <template #icon>
            <ChevronRightIcon :class="{ expanded: !isCollapsed }" />
          </template>
        </BaseButton>
        <div v-else class="title">
          {{ title }}
          <span
            v-if="displayedTaskIds && displayedTaskIds.length > 0"
            class="task-count"
            :class="{ 'task-count--overdue': isOverdue }"
            >{{ displayedTaskIds.length }}</span
          >
        </div>
      </div>
      <div v-if="!isCollapsed">
        <AddTaskButton
          v-if="(week || date) && showAddButton"
          ref="addTaskButtonEl"
          key="add"
          class="task-button"
          size="compact"
          :tabindex="-1"
          :add-task-action="addTaskAction"
          :post-validate-action="selectTaskById"
          :metrics-source="'planner/sidebar'"
          @focus="unselectTask"
        />
        <Ordering
          :list-key="listKey"
          :ids="displayedTaskIds ?? []"
          :disabled="isEditing"
          :reorderable="!!listKey"
          :managed="false"
          @reorder="onReorder"
        >
          <template #item="{ id }">
            <div class="task-inline-container">
              <TaskInline
                :actions="taskActions"
                compact
                :is-editing="isEditing"
                :is-selected="id === selectedTaskId"
                :task-id="id"
                :external-drag-class="draggableTaskClassName"
                :reorderable="!!listKey"
                :reserve-reorder-column="true"
                batch
                :hide-scheduled-date="hideScheduledDate"
                @quit-editing="quitEditing"
                @select="selectTaskById(id)"
                @set-selected-task-el="setSelectedTaskEl"
              />
            </div>
          </template>
        </Ordering>
      </div>
    </DraggableContainer>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs, watch } from "vue";
import { DraggableContainer } from "vue-shopify-draggable";

import type { ConsoleActionTask, TaskId, Week } from "@ctrl/controller-rpc";

import AddTaskButton from "@/components/AddTaskButton.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import * as inject from "@/components/inject";
import { draggableTaskClassName } from "@/components/planner/controllers/draggable";
import type { TaskActions } from "@/components/task/types";
import { useTaskList } from "@/components/task/useTaskList";
import TaskInline from "@/components/TaskInline.vue";
import { useOrderableTaskList } from "@/components/tasks/ordering";
import Ordering from "@/components/tasks/Ordering.vue";
import { useReleaser } from "@/ref";

export default defineComponent({
  components: {
    AddTaskButton,
    BaseButton,
    ChevronRightIcon,
    DraggableContainer,
    Ordering,
    TaskInline,
  },
  props: {
    isDraggingTask: { type: Boolean, required: true },
    dropZoneId: { type: String, required: false, default: undefined },
    isFootTaskListActive: { type: Boolean, required: true },
    isOutsideDropZone: { type: Boolean, required: true },
    taskIds: {
      type: Array as PropType<TaskId[] | null>,
      required: false,
      default: null,
    },
    week: { type: Object as PropType<Week>, required: false },
    /** When set (e.g. today), show Add task and schedule onto this date. */
    date: { type: Date, required: false, default: undefined },
    isCollapsable: { type: Boolean, required: false, default: false },
    isCollapsed: { type: Boolean, required: false, default: false },
    title: { type: String, required: false, default: "" },
    showAddButton: { type: Boolean, required: false, default: true },
    hideScheduledDate: { type: Boolean, required: false, default: false },
    isOverdue: { type: Boolean, required: false, default: false },
    /** Shared ranking key; omit for Overdue / Upcoming (controller order). */
    listKey: { type: String, required: false, default: undefined },
  },
  emits: {
    unselectItems: () => true,
    isActive: (_: boolean) => true,
    "update:isCollapsed": (_: boolean) => true,
  },
  setup(props, { emit }) {
    const releaser = useReleaser();
    const { isFootTaskListActive, taskIds, listKey, date } = toRefs(props);
    const taskActions: TaskActions = {
      drag: false,
      hierarchy: true,
      notes: true,
      open: true,
      postpone: true,
      schedule: {
        able: true,
        type: "shortest",
      },
    };
    const console = inject.consoleModal();
    const controller = inject.controller();
    const contextMenu = inject.contextMenu();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const listKeyRef = computed(() => listKey.value);
    const {
      orderedIdsReadonly,
      commitOrder,
      release: releaseOrdering,
    } = useOrderableTaskList(listKeyRef, taskIds);
    releaser.defer(releaseOrdering);
    const displayedTaskIds = computed(() => {
      const ids = taskIds.value;
      if (!ids) return ids;
      return listKey.value ? orderedIdsReadonly.value : ids;
    });
    const selectableTaskIds = computed(() => displayedTaskIds.value);

    const onReorder = (visibleAfter: string[]) => {
      if (listKey.value) commitOrder(visibleAfter);
    };

    const addTaskAction = computed<ConsoleActionTask>(() => {
      if (date.value) {
        return { type: "task-scheduling", date: date.value };
      }
      if (props.week) {
        return { type: "task-postponing", batch: props.week };
      }
      return { type: "task" };
    });
    const isSelectable = computed(() =>
      isFootTaskListActive.value
        ? { withUp: false, withDown: false }
        : { withUp: true, withDown: true }
    );
    const unselectItems = () => {
      emit("unselectItems");
    };
    const {
      addTaskButtonEl,
      isEditing,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskListEl,
      unselectTask,
      release: releaseTaskList,
    } = useTaskList(
      selectableTaskIds,
      {
        controller,
        console,
        contextMenu,
        isModalShown,
        isInputFocused,
      },
      {
        unselectItems,
        isSelectable,
      }
    );
    releaser.defer(releaseTaskList);

    // Maintain a value to know if this list is active i.e a least
    // one task is selected.
    const isActive = ref(false);
    watch(selectedTaskId, (newId) => {
      const wasActive = isActive.value;
      isActive.value = !!newId;
      if (wasActive !== isActive.value) {
        emit("isActive", isActive.value);
      }
    });

    return {
      addTaskAction,
      addTaskButtonEl,
      draggableTaskClassName,
      isEditing,
      onReorder,
      quitEditing,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      displayedTaskIds,
      taskActions,
      taskListEl,
      unselectTask,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/itemslist.scss";

.title-container {
  display: flex;
  align-items: center;
  gap: 1px;
  margin-inline-start: 0;
  padding-block: 2px;
  // Align with compact TaskInline checkboxes (3px row inset + 28px handle
  // column). The handle sits in the gutter to the left of the title.
  padding-inline-start: calc(3px + 28px);

  // Auxiliary collapse button pads 8px inline — pull back so the label matches.
  &:has(.collapse-button) {
    padding-inline-start: calc(3px + 28px - 8px);
  }

  .collapse-button {
    :deep(svg) {
      transition: transform 0.2s ease;

      &.expanded {
        transform: rotate(90deg);
      }
    }
  }
}

.title {
  @extend %font-body-medium;
  display: flex;
  align-items: center;
  gap: 8px;
  // No inline-start pad — title-container already aligns with checkboxes.
  padding: 4px 8px 4px 0;
  @include themed() {
    color: t($primary);
  }
}

.task-count {
  font-weight: 300;
  @include themed() {
    color: t($grey-40);
  }

  &--overdue {
    padding: 1px 6px;
    margin-inline-start: -2px;
    border-radius: $border-radius-m;
    @include themed() {
      background-color: t($secondary-10);
      color: t($secondary-50);
    }
  }
}

:deep(.collapse-button) {
  @include themed() {
    color: t($primary) !important;
  }

  .left {
    width: 100%;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-inline-end: 4px;
    @extend %font-body-medium;
  }
}

:deep(button.base-button.none.auxiliary.default) {
  @include themed() {
    color: t($primary) !important;
  }
}

.tasklist-container {
  position: relative;
  padding: 4px 2px 0 2px;
  display: flex;
  max-width: 100%;
  flex-direction: column;
  box-sizing: border-box;

  .base-skeleton-list {
    margin-top: 12px;
    margin-inline-start: 8px;
  }

  .taskslist {
    flex: 1;
    width: 100%;
    max-width: 100%;
    border: 1px solid transparent;

    .task-inline {
      box-sizing: border-box;
      :deep(.base-button.hierarchy) {
        padding: 2px 6px;

        .text {
          @extend %font-small-regular;
        }
      }
    }

    .task-inline-container {
      padding: 0 3px;
    }

    &.isDraggingTask:not(.isOutsideDropZone) {
      border-radius: $border-radius-l;
      @include transition(border 0.2s ease-out);
      @include themed() {
        border: 0.5px dashed t($secondary-50);
      }
    }
  }
}

.task-button {
  // Align with TaskInline compact handle column (6 + 14 + 8 = 28px):
  // container inset 3px + column − AddTaskButton compact pad 8px.
  margin-inline-start: 23px;
  width: calc(100% - 28px);
}
</style>
